import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { delay } from 'rxjs/operators';

import { AutoNumericDirective } from '../../../directives/auto-numeric.directive';
import { BaseUI } from '../../base.ui';
import { ErrorDialog } from '../../dialog/error/error.dialog';
import {
    GetExternalTradersUseCase,
    GetTradersUseCase,
    OrderAction,
    ProductInterface,
    SelectedProductInterface,
    TraderInterface,
} from '../../../../core';
import { ParentEvents } from '../../../utils/parent-events.service';
import { Id } from 'src/app/main/domain/data';

@Component({
    selector: 'thr-operate-external-transfer',
    templateUrl: './operate-external-transfer.component.html',
    styleUrls: ['./operate-external-transfer.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class OperateExternalTransferComponent extends BaseUI implements AfterViewInit, OnDestroy, OnInit {
    public OrderAction: typeof OrderAction = OrderAction;

    public externalTraders: TraderInterface[];
    public initialAmount;
    public initialShares;
    public initialTotal;
    public traders: TraderInterface[];

    @Input()
    editable = true;

    @Input()
    product: ProductInterface;

    @Input()
    selectedProduct: SelectedProductInterface;

    @ViewChild('totalAutoNumericDirective')
    totalAutoNumericDirective: AutoNumericDirective;

    @ViewChild('sharesDirective')
    sharesDirective: AutoNumericDirective;

    @ViewChild('amountDirective')
    amountDirective: AutoNumericDirective;

    @Input()
    canExonerate = false;

    @Output()
    detail: EventEmitter<Id> = new EventEmitter();

    @Output()
    exonerate: EventEmitter<Id> = new EventEmitter();


    constructor(
        parentEvents: ParentEvents,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private getTradersUseCase: GetTradersUseCase,
        private getExternalTradersUseCase: GetExternalTradersUseCase
    ) {
        super(parentEvents);
    }

    ngOnInit(): void {
        this.initialShares = this.selectedProduct.order.shares || 0;
        this.initialAmount = this.selectedProduct.order.amount || 0;
        this.initialTotal = this.selectedProduct.order.referenceAmount || 0;
        this.cdr.detectChanges();

        this.getTraders();
        this.getExternalTraders();
    }

    get minimumInvestment(): number {
        if (this.product) {
            if (this.selectedProduct.amount > 0) {
                return this.product.minimumContribution ? this.product.minimumContribution : 0.01;
            } else {
                return this.product.minimumInvestment ? this.product.minimumInvestment : 0.01;
            }
        }

        return 0;
    }

    get minimumInvestmentMsg(): string {
        if (this.product) {
            if (this.selectedProduct.amount > 0) {
                return 'form.validation.minimum-contribution';
            } else {
                return 'form.validation.minimum-amount';
            }
        }

        return null;
    }

    private getTraders() {
        this.getTradersUseCase
            .bind()
            .pipe(delay(1)) // prevent ExpressionChangedAfterItHasBeenCheckedError
            .subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.traders = transaction.data;
                    this.cdr.detectChanges();
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog);
                }
            });
    }

    private getExternalTraders() {
        this.getExternalTradersUseCase
            .bind()
            .pipe(delay(1)) // prevent ExpressionChangedAfterItHasBeenCheckedError
            .subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.externalTraders = transaction.data;
                    this.cdr.detectChanges();
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog);
                }
            });
    }

    onActionChange() {
        switch (this.selectedProduct.order.action) {
            case OrderAction.TOTAL:
                this.selectedProduct.order.withShares = false;
                this.onTotalChange();

                if (this.sharesDirective) {
                    this.sharesDirective.value = this.selectedProduct.order.shares;
                }
                if (this.amountDirective) {
                    this.amountDirective.value = this.selectedProduct.order.amount;
                }

                this.cdr.detectChanges();
                break;
            case OrderAction.SHARES:
                this.selectedProduct.order.withShares = true;
                this.selectedProduct.order.amount = 0;

                if (this.sharesDirective) {
                    this.sharesDirective.value = this.selectedProduct.order.shares;
                }
                if (this.amountDirective) {
                    this.amountDirective.value = this.selectedProduct.order.amount;
                }
                if (this.totalAutoNumericDirective) {
                    this.totalAutoNumericDirective.value = this.selectedProduct.order.referenceAmount;
                }

                this.cdr.detectChanges();
                break;
            case OrderAction.AMOUNT:
                this.selectedProduct.order.withShares = false;
                this.selectedProduct.order.shares = 0;
                this.onAmountChange();

                if (this.totalAutoNumericDirective) {
                    this.totalAutoNumericDirective.value = this.selectedProduct.order.referenceAmount;
                }

                this.cdr.detectChanges();
                break;
        }

        this.parentEvents.resize();
    }

    onTotalChange() {
        setTimeout(() => {
            if (this.totalAutoNumericDirective) {
                this.selectedProduct.order.referenceAmount = this.totalAutoNumericDirective.value;
            }

            this.cdr.detectChanges();
        });
    }

    onAmountChange() {
        setTimeout(() => {
            if (this.amountDirective) {
                this.selectedProduct.order.amount = this.amountDirective.value;
            }

            this.cdr.detectChanges();
        });
    }

    onShareChange() {
        setTimeout(() => {
            if (this.sharesDirective) {
                this.selectedProduct.order.shares = this.sharesDirective.value;
            }

            this.cdr.detectChanges();
        });
    }

    onExonerate() {
        this.exonerate.emit(this.product.id as Id);
    }
}
